import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { Button } from "semantic-ui-react";
import moment from "moment";
import FeatherIcon from "feather-icons-react";
import { AgendaByRut } from "src/graphql/agenda";
import { GetCollectionPaidInvoiceHistory } from "src/graphql/cobranza";
import { formatMoneyWithCurrency, formatRut } from "src/utils/formatter";
import { openDocumentDetail } from "src/utils/documentModal";
import CobranzaGestionMetaBar from "./CobranzaGestionMetaBar";
import {
  applyCobranzaDocumentOverrides,
  getCobranzaClientManagementDisplay,
  getCobranzaEmailHistoryEntries,
  getCobranzaEmailHistoryFacts,
  getCobranzaEmailHistoryInvoices,
  getCobranzaEmailHistorySummary,
  getPaidInvoiceDsoPresentation,
} from "./cobranzaGestionUtils";
import CobranzaEmailHistoryPreviewButton from "./CobranzaEmailHistoryPreviewButton";
import CobranzaEmailRecipientsStatus from "./CobranzaEmailRecipientsStatus";
import CobranzaClientEmailsDisplay from "./CobranzaClientEmailsDisplay";
import "./cobranzaGestionSidebar.scss";
import "./cobranzaGestionHistory.scss";

function formatDate(d) {
  if (!d) return "—";
  const m = moment(d);
  return m.isValid() ? m.format("DD/MM/YYYY HH:mm") : "—";
}

export default function CobranzaGestionSidebar({
  closeMenu,
  companyId,
  row,
  clientDocs = [],
  reexecuteQuery,
  onOpenClienteInfo,
  onOpenHistory,
  isExpanded = false,
  onToggleExpand,
}) {
  const { t } = useTranslation("cobranza");
  const history = useHistory();
  const location = useLocation();
  const rut = row?.issuerRut || "";
  const [historyTab, setHistoryTab] = useState("emails");
  const { data, fetching, reexecuteQuery: reexecuteAgendaQuery } = AgendaByRut(rut, companyId, "cl");
  const agenda = data?.AgendaByRut;
  const {
    data: paidHistoryData,
    fetching: paidHistoryFetching,
    reexecuteQuery: reexecutePaidHistoryQuery,
  } = GetCollectionPaidInvoiceHistory({
    companyId,
    receiverRut: rut,
  });
  const paidHistory = paidHistoryData?.getCollectionPaidInvoiceHistory;

  useEffect(() => {
    setHistoryTab("emails");
  }, [rut, companyId]);

  useEffect(() => {
    if (!rut || !companyId) return;
    reexecuteAgendaQuery?.({ requestPolicy: "network-only" });
    reexecutePaidHistoryQuery?.({ requestPolicy: "network-only" });
  }, [rut, companyId, reexecuteAgendaQuery, reexecutePaidHistoryQuery]);

  useEffect(() => {
    if (!rut || !companyId || historyTab !== "emails") return undefined;
    const timer = window.setInterval(() => {
      reexecuteAgendaQuery?.({ requestPolicy: "network-only" });
    }, 20000);
    return () => window.clearInterval(timer);
  }, [rut, companyId, historyTab, reexecuteAgendaQuery]);

  const docsWithManagement = useMemo(
    () => applyCobranzaDocumentOverrides(clientDocs, agenda || row?.agenda),
    [clientDocs, agenda, row?.agenda]
  );
  const clientManagement = useMemo(
    () => getCobranzaClientManagementDisplay(agenda || row?.agenda, docsWithManagement),
    [agenda, row?.agenda, docsWithManagement]
  );

  const openDocIds = useMemo(
    () =>
      (docsWithManagement || [])
        .filter((d) => Number(d.remaining || 0) > 0 && d.id)
        .map((d) => String(d.id)),
    [docsWithManagement]
  );

  const emails = useMemo(() => {
    return getCobranzaEmailHistoryEntries(agenda);
  }, [agenda]);

  const mergedRow = useMemo(
    () => ({
      ...row,
      agenda: agenda
        ? {
            ...row?.agenda,
            ...agenda,
            id: agenda.id,
            collectionDecision: clientManagement.collectionDecision ?? null,
            collectionChannel: clientManagement.collectionChannel ?? null,
          }
        : row?.agenda
          ? {
              ...row.agenda,
              collectionDecision: clientManagement.collectionDecision ?? null,
              collectionChannel: clientManagement.collectionChannel ?? null,
            }
          : row?.agenda,
    }),
    [row, agenda, clientManagement]
  );

  const handleRefresh = () => {
    reexecuteQuery?.({ requestPolicy: "network-only" });
    reexecuteAgendaQuery?.({ requestPolicy: "network-only" });
    reexecutePaidHistoryQuery?.({ requestPolicy: "network-only" });
  };

  const renderEmailHistory = (entry, index) => {
    const facts = getCobranzaEmailHistoryFacts(entry, t);
    const invoices = getCobranzaEmailHistoryInvoices(entry, t);

    return (
      <li key={`email-${index}-${entry?.date}`} className="cf-gestion-email-history__entry">
        <span className="cf-gestion-timeline__date">{formatDate(entry?.date)}</span>
        <div className="cf-gestion-email-history__content">
          <span className="cf-gestion-timeline__text">
            {entry?.userEmail || "Usuario"} envió correo de cobranza
          </span>
          <div className="cf-gestion-email-history__subject">
            {getCobranzaEmailHistorySummary(entry, t)}
          </div>
          <CobranzaEmailRecipientsStatus entry={entry} />
          <div className="cf-gestion-email-history__actions">
            <CobranzaEmailHistoryPreviewButton entry={entry} />
          </div>
          {facts.length > 0 && (
            <div className="cf-gestion-email-history__facts">
              {facts.map((fact) => (
                <span key={`${fact.label}-${fact.value}`} className="cf-gestion-email-history__fact">
                  <strong>{fact.label}:</strong> {fact.value}
                </span>
              ))}
            </div>
          )}
          {invoices.length > 0 && (
            <ul className="cf-gestion-email-history__invoice-list">
              {invoices.map((invoice, invoiceIndex) => (
                <li
                  key={`${invoice.documentId || invoice.label}-${invoiceIndex}`}
                  className="cf-gestion-email-history__invoice-item"
                >
                  <span className="cf-gestion-email-history__invoice-label">{invoice.label}</span>
                  <span className="cf-gestion-email-history__invoice-meta">
                    Vence {invoice.dueDateLabel}
                    {invoice.amountLabel ? ` · ${invoice.amountLabel}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </li>
    );
  };

  const renderPaidInvoiceHistory = (item, index) => {
    const dsoUi = getPaidInvoiceDsoPresentation(item?.dsoDays);
    const dueLabel = item?.dueDate
      ? moment(item.dueDate).format("DD/MM/YYYY")
      : "—";
    const paymentLabel = item?.paymentDate
      ? moment(item.paymentDate).format("DD/MM/YYYY")
      : "—";
    const amountLabel =
      item?.originalAmount != null
        ? formatMoneyWithCurrency(item.originalAmount, "CLP")
        : "";
    const docMongoId = item?.id ? String(item.id) : "";
    const folio = item?.folio || item?.documentId;
    const typeLabel = item?.documentLabel || "Factura";

    const openPaidInvoiceDetail = (e) => {
      e.stopPropagation();
      if (!docMongoId) return;
      openDocumentDetail(docMongoId, history, location);
    };

    return (
      <li
        key={`paid-${docMongoId || item?.documentId || item?.folio || index}`}
        className="cf-gestion-paid-history__entry"
      >
        <span className="cf-gestion-timeline__date">{paymentLabel}</span>
        <div className="cf-gestion-paid-history__content">
          <span className="cf-gestion-timeline__text cf-gestion-paid-history__title">
            {typeLabel}
            {folio ? (
              <>
                {" "}
                {docMongoId ? (
                  <button
                    type="button"
                    className="cf-gestion-invoice-doc-link"
                    title="Ver información del documento"
                    onClick={openPaidInvoiceDetail}
                  >
                    #{folio}
                  </button>
                ) : (
                  <span> #{folio}</span>
                )}
              </>
            ) : null}
          </span>
          <div className="cf-gestion-paid-history__meta">
            <span className="cf-gestion-paid-history__meta-item">
              <strong>Vence:</strong> {dueLabel}
            </span>
            {amountLabel ? (
              <span className="cf-gestion-paid-history__meta-item">
                <strong>Monto:</strong> {amountLabel}
              </span>
            ) : null}
            <span className={`cf-gestion-paid-history__dso ${dsoUi.className}`}>
              {dsoUi.text}
            </span>
          </div>
        </div>
      </li>
    );
  };

  return (
    <div className="cf-gestion-sidebar bitacora-panel">
      <div className="cf-gestion-sidebar-header">
        <div className="bitacora-panel-header-left">
          <div className="bitacora-panel-header-title-wrap">
            <h2 className="bitacora-panel-title">{t("gestionSidebar.title")}</h2>
            <div className="bitacora-panel-sub text-truncate">
              {row?.issuerName || t("gestionSidebar.client")}
            </div>
            {onOpenHistory && (
              <button
                type="button"
                className="cf-gestion-history-btn"
                onClick={onOpenHistory}
                title={t("gestionSidebar.viewHistory")}
              >
                {t("gestionSidebar.history")}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="5" y1="19" x2="19" y2="5" />
                  <polyline points="7 5 19 5 19 17" />
                </svg>
              </button>
            )}
          </div>
        </div>
        <div className="bitacora-panel-header-right cf-gestion-sidebar-header-actions">
          {onToggleExpand && (
            <button
              type="button"
              onClick={onToggleExpand}
              className="bitacora-panel-expand-btn"
              title={isExpanded ? "Achicar" : "Expandir"}
            >
              <FeatherIcon icon={isExpanded ? "minimize-2" : "maximize-2"} size={20} />
            </button>
          )}
          {closeMenu && (
            <button type="button" onClick={closeMenu} className="bitacora-panel-close-btn" title="Cerrar">
              <FeatherIcon icon="x" size={20} />
            </button>
          )}
        </div>
      </div>

      <div className="bitacora-panel-content cf-gestion-sidebar-content">
        {fetching && !agenda ? (
          <p className="cf-gestion-detail-panel__loading">Cargando…</p>
        ) : (
          <>
            <section className="cf-gestion-sidebar-section cf-gestion-sidebar-section--cliente">
              <div className="cf-gestion-sidebar-section-head">
                <h3>Cliente</h3>
                <Button size="mini" basic color="teal" onClick={() => onOpenClienteInfo?.(row)}>
                  Editar datos
                </Button>
              </div>
              <dl className="cf-gestion-detail-dl">
                <dt>RUT</dt>
                <dd>{row?.issuerRut ? formatRut(String(row.issuerRut)) : "—"}</dd>
                <dt>Estado cliente</dt>
                <dd>{agenda?.collectionClientStatus || row?.agenda?.collectionClientStatus || "—"}</dd>
                <dt>Email</dt>
                <dd>
                  <CobranzaClientEmailsDisplay agenda={agenda || row?.agenda} />
                </dd>
                <dt>Teléfono</dt>
                <dd>{agenda?.contactInfo?.phone || "—"}</dd>
              </dl>
              <div className="cf-gestion-meta-bar-slot cf-gestion-meta-bar-slot--start">
                <CobranzaGestionMetaBar
                  row={mergedRow}
                  companyId={companyId}
                  openDocumentIds={openDocIds}
                  showChannel={false}
                  compact
                  processReadOnly
                  onUpdated={handleRefresh}
                />
              </div>
            </section>

            <section className="cf-gestion-sidebar-section cf-gestion-sidebar-section--history">
              <div className="cf-gestion-sidebar-tabs" role="tablist" aria-label={t("gestionDetail.historyAria")}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={historyTab === "emails"}
                  className={`cf-gestion-sidebar-tab${
                    historyTab === "emails" ? " cf-gestion-sidebar-tab--active" : ""
                  }`}
                  onClick={() => setHistoryTab("emails")}
                >
                  {t("gestionDetail.emailsSent", { count: emails.length })}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={historyTab === "paid"}
                  className={`cf-gestion-sidebar-tab${
                    historyTab === "paid" ? " cf-gestion-sidebar-tab--active" : ""
                  }`}
                  onClick={() => setHistoryTab("paid")}
                >
                  {t("gestionDetail.paidInvoices", { count: paidHistory?.totalCount ?? 0 })}
                </button>
              </div>

              {historyTab === "emails" ? (
                emails.length === 0 ? (
                  <p className="cf-gestion-detail-empty">{t("gestionDetail.noEmails")}</p>
                ) : (
                  <ul className="cf-gestion-timeline">
                    {emails.map((e, i) => renderEmailHistory(e, i))}
                  </ul>
                )
              ) : paidHistoryFetching && !paidHistory ? (
                <p className="cf-gestion-detail-panel__loading">{t("gestionDetail.loadingPaid")}</p>
              ) : (paidHistory?.items || []).length === 0 ? (
                <p className="cf-gestion-detail-empty">
                  {t("gestionDetail.noPaidInvoices")}
                </p>
              ) : (
                <>
                  {paidHistory?.avgDso != null && (
                    <p className="cf-gestion-paid-history__summary">
                      Promedio DSO:{" "}
                      <span
                        className={
                          getPaidInvoiceDsoPresentation(paidHistory.avgDso).className
                        }
                      >
                        {getPaidInvoiceDsoPresentation(paidHistory.avgDso).text}
                      </span>
                      {" · "}
                      {paidHistory.totalCount}{" "}
                      {paidHistory.totalCount === 1 ? "factura" : "facturas"}
                    </p>
                  )}
                  <ul className="cf-gestion-timeline cf-gestion-paid-history">
                    {(paidHistory?.items || []).map((item, i) =>
                      renderPaidInvoiceHistory(item, i)
                    )}
                  </ul>
                </>
              )}
            </section>

          </>
        )}
      </div>
    </div>
  );
}
